import { useState, useEffect, useCallback } from 'react';
import { Button, Typography, Tag } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import ReactConfetti from 'react-confetti';
import {
    RightOutlined,
    FastForwardOutlined,
    UserOutlined,
    BankOutlined,
    CheckCircleFilled,
} from '@ant-design/icons';
import { FaTrophy } from 'react-icons/fa';
import { IAwardWithWinner } from '../../../Steps/ImportData/useImportData';
import { IRulesGiveaway } from '../../../../../../interfaces';
import { awardImagesUrl } from '../../../../../../services/urls';
import { convertMoedaBRDoJSNovo } from '../../../../../../utils/Conversores';
import ImagemLogo from '../../../../../../assets/images/logo-lateral.png';
import ReelSpinner from './ReelSpinner';
import { soundFx } from '../utils/soundSynthesizer';

const { Title, Text } = Typography;

interface DrawStageProps {
    awards: IAwardWithWinner[];
    rules?: IRulesGiveaway;
    cityName?: string;
    onFinishAll: () => void;
}

export default function DrawStage({ awards, rules, cityName, onFinishAll }: DrawStageProps) {
    const [currentIndex, setCurrentIndex] = useState<number>(0);
    const [isSpinning, setIsSpinning] = useState<boolean>(true);
    const [isRevealed, setIsRevealed] = useState<boolean>(false);
    const [showConfetti, setShowConfetti] = useState<boolean>(false);

    const formatCityTitle = (name?: string) => {
        if (!name) return '';
        const trimmed = name.trim();
        if (trimmed.toLowerCase().startsWith('prefeitura')) {
            return trimmed;
        }
        return `Prefeitura de ${trimmed}`;
    };

    const currentAward = awards[currentIndex];
    const totalAwards = awards.length;
    const isLastAward = currentIndex === totalAwards - 1;

    // Start drawing round
    const startDrawingRound = useCallback(() => {
        setIsRevealed(false);
        setIsSpinning(true);
        setShowConfetti(false);
        soundFx.playRiser(1400);
    }, []);

    useEffect(() => {
        startDrawingRound();
    }, [currentIndex, startDrawingRound]);

    // When all lottery balls lock
    const handleReelComplete = useCallback(() => {
        setIsSpinning(false);
        setIsRevealed(true);
        setShowConfetti(true);
        soundFx.playFanfare();

        setTimeout(() => {
            setShowConfetti(false);
        }, 4500);
    }, []);

    // Next prize (Manual mode only)
    const handleNextPrize = useCallback(() => {
        if (isLastAward) {
            onFinishAll();
        } else {
            setIsRevealed(false);
            setIsSpinning(true);
            setShowConfetti(false);
            setCurrentIndex(prev => prev + 1);
        }
    }, [isLastAward, onFinishAll]);

    if (!currentAward) {
        return null;
    }

    // Resolve ticket and winner info safely
    const winningTicket =
        currentAward.winner?.winning_ticket_id ??
        (currentAward as any).winning_ticket_id ??
        (currentAward as any).winning_ticket_auto_id ??
        '000000';

    const winnerName =
        currentAward.winner?.app_user_name ??
        (currentAward as any).app_user?.name ??
        (currentAward as any).app_user_auto?.name ??
        'Contemplado(a)';

    const socialInstitutionName =
        currentAward.winner?.social_institution_name ??
        (currentAward as any).social_institution_name;

    const rankNumber = currentIndex + 1;
    const rankBadgeClass =
        rankNumber === 1
            ? 'rank-badge-1'
            : rankNumber === 2
                ? 'rank-badge-2'
                : rankNumber === 3
                    ? 'rank-badge-3'
                    : 'rank-badge-default';

    return (
        <div
            style={{
                maxWidth: 1200,
                width: '100%',
                margin: '0 auto',
                padding: '20px 16px',
                position: 'relative',
            }}
        >
            {showConfetti && (
                <ReactConfetti
                    style={{
                        zIndex: 10001,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        pointerEvents: 'none',
                    }}
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={750}
                    gravity={0.16}
                    tweenDuration={4500}
                    initialVelocityY={22}
                    initialVelocityX={14}
                />
            )}

            {/* Layout Container: Main Stage + Sidebar of past winners */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) 340px',
                    gap: 20,
                    alignItems: 'start',
                }}
                className="live-draw-grid-layout"
            >
                {/* Left / Center: Main Draw Stage */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Header / Progress Breadcrumb */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            padding: '10px 16px',
                            background: '#ffffff',
                            borderRadius: 12,
                            border: '1px solid #e2e8f0',
                            flexWrap: 'wrap',
                            gap: 10,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                                className={rankBadgeClass}
                                style={{
                                    padding: '4px 12px',
                                    borderRadius: 16,
                                    fontSize: 13,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                }}
                            >
                                <FaTrophy size={13} /> {rankNumber}º Prêmio
                            </div>
                            <span style={{ fontSize: 13, color: '#64748b' }}>
                                de {totalAwards} prêmios
                            </span>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <img
                                src={ImagemLogo}
                                alt="Nota Município"
                                style={{ height: 20, objectFit: 'contain' }}
                            />
                            {cityName && (
                                <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                                    • {formatCityTitle(cityName)}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Main Stage Card */}
                    <div
                        className="municipal-card"
                        style={{
                            width: '100%',
                            padding: '24px 20px',
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 18,
                            position: 'relative',
                            background: '#ffffff',
                        }}
                    >
                        {/* Prize Info (Photo + Title) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                            <motion.div
                                key={currentAward.id}
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.25 }}
                                style={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 14,
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                {currentAward.images && currentAward.images.length > 0 ? (
                                    <img
                                        src={`${awardImagesUrl}/${currentAward.images[0].img}`}
                                        alt={currentAward.description}
                                        style={{ width: '85%', height: '85%', objectFit: 'contain' }}
                                    />
                                ) : (
                                    <FaTrophy color="#f59e0b" size={32} />
                                )}
                            </motion.div>

                            <div>
                                <Title
                                    level={3}
                                    style={{
                                        color: '#0f172a',
                                        margin: 0,
                                        fontWeight: 800,
                                        fontSize: 20,
                                    }}
                                >
                                    {currentAward.description}
                                </Title>
                            </div>
                        </div>

                        {/* Spinning Status */}
                        <div style={{ minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AnimatePresence mode="wait">
                                {isSpinning ? (
                                    <motion.div
                                        key="spinning"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            color: '#0284c7',
                                            fontSize: 13,
                                            fontWeight: 600,
                                        }}
                                    >
                                        <span
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                background: '#0284c7',
                                                animation: 'pulse 1s infinite',
                                            }}
                                        />
                                        Extraindo bilhete premiado...
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="revealed"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            color: '#16a34a',
                                            fontSize: 13,
                                            fontWeight: 700,
                                        }}
                                    >
                                        <CheckCircleFilled /> Bilhete Homologado!
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Lottery Balls Drum */}
                        <ReelSpinner
                            key={`reel-${currentAward.id}-${currentIndex}`}
                            targetTicket={winningTicket}
                            isSpinning={isSpinning}
                            onComplete={handleReelComplete}
                        />

                        {/* Winner Reveal Card */}
                        <div style={{ width: '100%', minHeight: 80 }}>
                            <AnimatePresence>
                                {isRevealed && !isSpinning && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.3 }}
                                        style={{
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 12,
                                            padding: '16px 20px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 10,
                                        }}
                                    >
                                        {rules?.show_winner_name && winnerName && (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <UserOutlined style={{ color: '#2563eb', fontSize: 14 }} />
                                                <Text type="secondary" style={{ color: '#64748b', fontSize: 13 }}>
                                                    Contemplado(a):
                                                </Text>
                                                <span style={{ color: '#0f172a', fontWeight: 800, fontSize: 16 }}>
                                                    {winnerName}
                                                </span>
                                            </div>
                                        )}

                                        {rules?.show_social_institution_name && socialInstitutionName && (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                <BankOutlined style={{ color: '#16a34a', fontSize: 14 }} />
                                                <Text type="secondary" style={{ color: '#64748b', fontSize: 13 }}>
                                                    Instituição Beneficiada:
                                                </Text>
                                                <span style={{ color: '#0f172a', fontWeight: 800, fontSize: 16 }}>
                                                    {socialInstitutionName}
                                                </span>
                                                {currentAward.social_institution_value && Number(currentAward.social_institution_value) > 0 && (
                                                    <Tag color="green" style={{ borderRadius: 6, fontWeight: 700, fontSize: 12, margin: '0 0 0 4px' }}>
                                                        + R$ {convertMoedaBRDoJSNovo(currentAward.social_institution_value)}
                                                    </Tag>
                                                )}
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Manual Controls Bar (No reload button) */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 14,
                            flexWrap: 'wrap',
                            width: '100%',
                        }}
                    >
                        <Button
                            type="primary"
                            size="large"
                            icon={isLastAward ? <FaTrophy /> : <RightOutlined />}
                            disabled={isSpinning}
                            onClick={handleNextPrize}
                            style={{
                                height: 48,
                                padding: '0 32px',
                                fontSize: 15,
                                fontWeight: 700,
                                borderRadius: 10,
                                background: '#2563eb',
                                border: 'none',
                                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            {isLastAward
                                ? 'Finalizar e Ver Lista de Ganhadores'
                                : 'Ver Próximo Ganhador ➔'}
                        </Button>

                        <Button
                            type="text"
                            size="large"
                            icon={<FastForwardOutlined />}
                            onClick={onFinishAll}
                            style={{
                                height: 48,
                                color: '#64748b',
                                fontWeight: 500,
                            }}
                        >
                            Pular para Lista Final
                        </Button>
                    </div>
                </div>

                {/* Right Corner: Organized Panel of Sorteados / Histórico */}
                <div className="live-draw-history-panel">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 12,
                            paddingBottom: 8,
                            borderBottom: '1px solid #e2e8f0',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaTrophy size={14} color="#f59e0b" />
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                Últimos Sorteados
                            </span>
                        </div>
                        <Tag color="blue" style={{ borderRadius: 10, fontSize: 11, fontWeight: 700, margin: 0 }}>
                            {Math.min(currentIndex + (isRevealed ? 1 : 0), totalAwards)} / {totalAwards}
                        </Tag>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {awards.map((award, idx) => {
                            const isPast = idx < currentIndex || (idx === currentIndex && isRevealed);
                            const isCurrent = idx === currentIndex;
                            const isFuture = idx > currentIndex;

                            const ticket =
                                award.winner?.winning_ticket_id ??
                                (award as any).winning_ticket_id ??
                                (award as any).winning_ticket_auto_id ??
                                '---';

                            const winner =
                                award.winner?.app_user_name ??
                                (award as any).app_user?.name ??
                                (award as any).app_user_auto?.name ??
                                'Contemplado(a)';

                            return (
                                <div
                                    key={award.id || idx}
                                    className={`live-draw-history-item ${isCurrent ? 'active-item' : ''}`}
                                    style={{
                                        opacity: isFuture ? 0.45 : 1,
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                                        <div
                                            style={{
                                                width: 22,
                                                height: 22,
                                                borderRadius: 6,
                                                background: isPast ? '#dcfce7' : isCurrent ? '#dbeafe' : '#f1f5f9',
                                                color: isPast ? '#15803d' : isCurrent ? '#1d4ed8' : '#94a3b8',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {isPast ? <CheckCircleFilled style={{ fontSize: 12 }} /> : idx + 1}
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: isCurrent ? 700 : 600,
                                                    color: isCurrent ? '#1d4ed8' : '#1e293b',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {award.description}
                                            </span>

                                            {isPast && rules?.show_winner_name && (
                                                <span
                                                    style={{
                                                        fontSize: 11,
                                                        color: '#64748b',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {winner}
                                                </span>
                                            )}

                                            {isCurrent && !isRevealed && (
                                                <span style={{ fontSize: 11, color: '#0284c7', fontWeight: 600 }}>
                                                    Sorteando agora...
                                                </span>
                                            )}

                                            {isFuture && (
                                                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                                                    Aguardando
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {isPast && (
                                        <Tag
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 800,
                                                borderRadius: 6,
                                                margin: 0,
                                                padding: '2px 8px',
                                                fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                                color: '#1d4ed8',
                                                background: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                            }}
                                        >
                                            {ticket}
                                        </Tag>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
